// src/components/services/servicePackages.js
// -----------------------------------------------------------------------------
// SINGLE SOURCE OF TRUTH for the fixed service packages (the landing cards).
//
// Used by:
//   - src/components/services/Services.jsx   (public landing cards) -> ./servicePackages
//   - server/routes/payments.js              (Stripe charge — mirror the same
//                                             `id` + price)
//
// Three tiers: Entrepreneur Launchpad / Small Business Studio / Enterprise Systems Lab.
//
// Field notes:
//   price       -> button label shown on the card (kept as 'Get Quote')
//   priceValue  -> "from" price in WHOLE DOLLARS (used by calculator / checkout)
//   interval    -> 'one_time' or 'month'
//   color       -> card CSS class (service-card--{color}); kept blue/pink/cyan
//                  so existing styles are untouched
// -----------------------------------------------------------------------------

export const categories = {
  entrepreneur: {
    title: 'Entrepreneur Launchpad',
    icon: 'uil-rocket',
    services: [
      {
        id: 'ent-web-presence',
        fulfillment: 'consult',
        name: 'Signature Web Presence',
        price: 'Get Quote',
        priceValue: 1200,
        interval: 'one_time',
        description: 'Look established from day one',
        features: [
          'Custom website, Shopify store, or portfolio',
          'Mobile-first & lightning fast',
          'Your own domain & business email',
          'Google-ready (local SEO basics)',
          'Social links & contact built in',
          '30 days support'
        ],
        delivery: '5-7 days',
        color: 'blue',
        ideal: 'Perfect for: Stylists, creators, side-hustles'
      },
      {
        id: 'ent-booking',
        fulfillment: 'consult',
        name: 'Booking & Payments Engine',
        price: 'Get Quote',
        priceValue: 900,
        interval: 'one_time',
        description: 'Let clients book and pay you 24/7',
        features: [
          'Online booking calendar',
          'Stripe deposits & payments',
          'Automatic confirmations',
          'SMS/email reminders (cut no-shows)',
          'Works for salons, barbers, events, travel',
          '30 days support'
        ],
        delivery: '5-7 days',
        popular: true,
        color: 'blue',
        savings: 'No more back-and-forth booking'
      },
      {
        id: 'ent-ai-assistant',
        fulfillment: 'consult',
        name: 'AI Growth Assistant',
        price: 'Get Quote',
        priceValue: 99,
        interval: 'month',
        description: 'Your AI sidekick for clients & marketing',
        features: [
          'AI chat trained on your business',
          'Captures & qualifies leads 24/7',
          'Automated email marketing',
          'Follow-up sequences on autopilot',
          'Installs on your existing site'
        ],
        delivery: '2-3 days',
        color: 'blue',
        savings: 'Never lose a lead again'
      },
      {
        id: 'ent-marketing',
        fulfillment: 'consult',
        name: 'Growth Marketing Engine',
        price: 'Get Quote',
        priceValue: 499,
        interval: 'month',
        description: 'Get your first customers with Google & Facebook ads',
        features: [
          'Google Ads setup & management',
          'Facebook & Instagram ad campaigns',
          'Audience targeting & retargeting',
          'Ad copy & creative included',
          'Landing page optimization',
          'Monthly performance reports'
        ],
        delivery: 'Ongoing',
        color: 'blue',
        savings: 'Turn ad spend into booked clients'
      }
    ]
  },

  smallbusiness: {
    title: 'Small Business Studio',
    icon: 'uil-shop',
    services: [
      {
        id: 'sb-web-platform',
        fulfillment: 'consult',
        name: 'Dynamic Web Platform',
        price: 'Get Quote',
        priceValue: 3500,
        interval: 'one_time',
        description: 'A real custom web app — not a template',
        features: [
          'Custom MERN web application',
          'User accounts & dashboards',
          'Live data & admin panel',
          'Restaurant, shop, or service-ready',
          'Stripe payments built in',
          'Scalable architecture',
          '90 days support'
        ],
        delivery: '2-3 weeks',
        color: 'pink',
        ideal: 'For: Restaurants, shops, service businesses'
      },
      {
        id: 'sb-automation',
        fulfillment: 'consult',
        name: 'Operations Automation Suite',
        price: 'Get Quote',
        priceValue: 1500,
        interval: 'one_time',
        description: 'Kill the busywork that eats your week',
        features: [
          'Automated order & lead alerts',
          'Invoice & reminder automation',
          'Inventory & low-stock flags',
          'CRM/lead routing',
          'Connects your existing tools',
          'Monthly tune-ups'
        ],
        delivery: '1-2 weeks',
        popular: true,
        color: 'pink',
        savings: 'Save 10+ hours a week'
      },
      {
        id: 'sb-booking-hub',
        fulfillment: 'consult',
        name: 'Booking & Customer Hub',
        price: 'Get Quote',
        priceValue: 1200,
        interval: 'one_time',
        description: 'Bookings, customers, reviews & loyalty in one',
        features: [
          'Appointments/reservations system',
          'Customer database (CRM-lite)',
          'Reviews & loyalty program',
          'Automated reminders',
          'Tailored to your business',
          '30 days support'
        ],
        delivery: '1-2 weeks',
        color: 'pink',
        ideal: 'Reservations for restaurants, appointments for shops'
      },
      {
        id: 'sb-marketing',
        fulfillment: 'consult',
        name: 'Local Marketing Engine',
        price: 'Get Quote',
        priceValue: 699,
        interval: 'month',
        description: 'Fill tables & appointments with local ads',
        features: [
          'Google Ads & Google Business optimization',
          'Facebook & Instagram local campaigns',
          'Geo-targeting to your neighborhood',
          'Promotions & seasonal campaigns',
          'Review & reputation boost',
          'Monthly performance reports'
        ],
        delivery: 'Ongoing',
        color: 'pink',
        savings: 'More foot traffic, every month'
      }
    ]
  },

  enterprise: {
    title: 'Enterprise Systems Lab',
    icon: 'uil-server-network',
    services: [
      {
        id: 'sys-api-builds',
        fulfillment: 'consult',
        name: 'API & Microservice Builds',
        price: 'Get Quote',
        priceValue: 4000,
        interval: 'one_time',
        description: 'Custom APIs that power and connect your stack',
        features: [
          'Custom ASP.NET Core REST APIs',
          'Microservice architecture',
          'Auth, rate limiting & security',
          'Full API documentation',
          'Connects your existing systems',
          'Deployment support'
        ],
        delivery: '2-4 weeks',
        color: 'cyan',
        note: 'Built on .NET'
      },
      {
        id: 'sys-crm-pipeline',
        fulfillment: 'consult',
        name: 'CRM & Lead Pipeline',
        price: 'Get Quote',
        priceValue: 2500,
        interval: 'one_time',
        description: 'Capture, route & enrich leads automatically',
        features: [
          'HubSpot/CRM integration',
          'Webhook & real-time syncing',
          'Lead capture & enrichment',
          'Zero manual data entry',
          'Pipeline automation',
          'Ongoing support'
        ],
        delivery: '1-2 weeks',
        popular: true,
        color: 'cyan',
        note: 'Proven — built this at scale before'
      },
      {
        id: 'sys-doc-api',
        fulfillment: 'consult',
        name: 'Document & Data Automation API',
        price: 'Get Quote',
        priceValue: 3000,
        interval: 'one_time',
        description: 'Generate, extract & sync data via clean APIs',
        features: [
          'Branded PDF generation API',
          'Invoice/form data extraction',
          'System-to-system data sync',
          'Clean REST endpoints',
          'Usage-based or flat pricing',
          'Documentation included'
        ],
        delivery: '2-3 weeks',
        color: 'cyan',
        note: 'Built on .NET'
      }
    ]
  }
};

// Flat lookup by id (e.g. servicePackages['ent-booking']) for checkout / pricing.
export const servicePackages = Object.values(categories).reduce((acc, cat) => {
  cat.services.forEach((service) => {
    acc[service.id] = service;
  });
  return acc;
}, {});

// -----------------------------------------------------------------------------
// CHECKOUT FORK: how a configured package decides between self-serve Stripe
// checkout and a booked consultation.
//   - Under CONSULT_THRESHOLD (configured total) -> self-serve payment
//   - At/over it, OR pkg.requiresConsult === true, OR a custom build -> consult
// Set `requiresConsult: true` on any individual service to force a consult
// regardless of price (e.g. an ongoing retainer you want to talk through first).
// -----------------------------------------------------------------------------
export const CONSULT_THRESHOLD = 500;

// How a box is fulfilled:
//   'self_serve' -> client can pay right away (Pay now + optional Contact)
//   'consult'    -> book a call / chat first, then an admin quote -> pay
// A box's own `fulfillment` wins. If absent, fall back to the price threshold.
export const serviceFulfillment = (pkg) => {
  if (!pkg) return 'consult';
  if (pkg.fulfillment) return pkg.fulfillment;
  if (pkg.requiresConsult) return 'consult';
  if (pkg.id === 'custom' || pkg.categoryId === 'custom') return 'consult';
  return (pkg.priceValue || 0) >= CONSULT_THRESHOLD ? 'consult' : 'self_serve';
};

export const isSelfServe = (pkg) => serviceFulfillment(pkg) === 'self_serve';

export const needsConsultation = (pkg) => {
  if (!pkg) return false;
  return serviceFulfillment(pkg) === 'consult';
};

// -----------------------------------------------------------------------------
// BOOKING: for consult boxes, how the discovery meeting is booked, by tier.
//   type 'call'     -> video call (Zoom auto-generated by Cal.com)
//   type 'inperson' -> in-person meeting (set the location on the Cal event)
//   fee             -> booking fee in WHOLE DOLLARS (0 = free). Charged by the
//                      Cal.com *paid* event (its Stripe app), not by our server.
//   calLink         -> the Cal.com event slug, i.e. the part after cal.com/.
//                      Cal handles the intake form, date/time, payment, Zoom
//                      link, and emails you the booking.
// Keep `fee` in sync with the price set on the Cal.com event.
//   ent- ids -> entrepreneur   sb- ids -> smallbusiness   sys- ids -> enterprise
// -----------------------------------------------------------------------------
export const BOOKING = {
  entrepreneur:  { type: 'call',     fee: 0,   calLink: 'spinosoftbits/intro-call' },
  smallbusiness: { type: 'call',     fee: 50,  calLink: 'spinosoftbits/strategy-call' },
  enterprise:    { type: 'inperson', fee: 150, calLink: 'spinosoftbits/in-person' },
};

export const bookingConfig = (pkg) => {
  const id = pkg && pkg.id ? pkg.id : '';
  if (id.startsWith('sb-'))  return BOOKING.smallbusiness;
  if (id.startsWith('sys-')) return BOOKING.enterprise;
  return BOOKING.entrepreneur;
};

export default categories;